import { utcDayStart } from "./monthMatrix";

/**
 * The calendar's density encoding (ticket #268): item-bearing days
 * fill with a blue ramp whose depth encodes the day's due-item count,
 * normalized PER MONTH — the month's lightest loaded day takes the
 * lightest shade, its heaviest the darkest. Pure like monthMatrix, so
 * it runs in vitest; the screen pairs the step with the theme's ramp
 * tokens (tokens.ts density1–density5, contrast-pinned).
 *
 * Accepted trade-off (per the ticket): per-month normalization means
 * the same count can shade differently in different months — the
 * encoding answers "which day is heaviest THIS month", not absolute
 * load.
 */

export type DensityStep = 1 | 2 | 3 | 4 | 5;

/** The ramp's shade count — also the distinct-count ceiling. */
export const DENSITY_SHADES = 5;

/**
 * The ramp's TOKEN NAMES, in step order — the screen indexes its
 * theme's colors with these (name-only strings: this module stays
 * react-native-free, so it cannot import the tokens themselves).
 */
export const DENSITY_TOKENS = [
  "density1",
  "density2",
  "density3",
  "density4",
  "density5",
] as const;

/**
 * Bucket the month-window rows into day → due-item count. Rows arrive
 * incomplete-only (done is filtered inside the query); the day key is
 * the UTC day of the dueAt, whatever time component it carries.
 */
export function densityByDay(
  rows: Array<{ dueAt: number }>,
): Map<number, number> {
  const counts = new Map<number, number>();
  for (const row of rows) {
    const key = utcDayStart(row.dueAt);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

/**
 * Map each distinct count to its ramp step 1–5 (lightest → darkest):
 * up to 5 distinct counts spread EVENLY across the whole ramp — {1,2,3}
 * renders steps 1/3/5, two counts take the ends — while more than 5
 * quantize the min→max range into 5 equal bands (band-mates share a
 * shade, the ceiling's documented cost). Degenerate min === max takes
 * the lightest shade: "1 todo = lightest" holds verbatim and nothing
 * divides by zero.
 */
export function shadeForCounts(
  counts: Iterable<number>,
): Map<number, DensityStep> {
  const distinct = [...new Set(counts)].sort((a, b) => a - b);
  const out = new Map<number, DensityStep>();
  const min = distinct[0];
  if (min === undefined) return out;
  const max = distinct[distinct.length - 1]!;
  if (distinct.length <= DENSITY_SHADES) {
    const k = distinct.length;
    distinct.forEach((c, i) => {
      const step = k === 1 ? 1 : Math.round(1 + (i * (DENSITY_SHADES - 1)) / (k - 1));
      out.set(c, step as DensityStep);
    });
    return out;
  }
  // Quantize: the inclusive min→max count range over DENSITY_SHADES
  // equal bands; a count's band index is its step.
  const band = (max - min + 1) / DENSITY_SHADES;
  for (const c of distinct) {
    const step = Math.min(
      DENSITY_SHADES,
      Math.floor((c - min) / band) + 1,
    ) as DensityStep;
    out.set(c, step);
  }
  return out;
}
